/**
 * Which App Check attestation outcome the login seam is facing (KB-3352).
 *
 * Read ONLY through public surfaces: `onTokenChanged` and `window.grecaptcha`
 * presence. Nothing here imports or patches `@firebase/*`; `firebaseService.ts`
 * hands this module its subscription.
 *
 * Why `onTokenChanged` and not a public `getToken` probe — verified by executing
 * the real `@firebase/app-check` 0.12.0, not by reading it:
 *
 *   - After an exchange 403 with NO valid token, every mint calls the listener's
 *     ERROR callback with `appCheck/initial-throttle` then `appCheck/throttled`,
 *     `customData: { time: '01d:00m:00s', httpStatus: 403 }`. Auth meanwhile
 *     receives a dummy token it sends anyway, which enforced Identity Toolkit
 *     refuses.
 *   - After a 403 on a forced refresh WHILE A VALID TOKEN EXISTS, the public
 *     `getToken` REJECTS with that same 403 error, yet Auth still receives the
 *     valid token and can sign in. The listener gets NEXT, not error. A probe
 *     would therefore call a working session refused, and tell its user that
 *     retrying cannot help when it can.
 *
 * TWO READINGS OF THAT ONE SURFACE, because a delivery describes the moment it
 * arrived, not the moment a sign-in fails (round-1 B1):
 *
 *   - The standing listener keeps the last delivery. It is the only place a
 *     refusal, its HTTP status and its throttle window ever appear, and a refusal
 *     counts as `rejected` only while the window the SDK announced is still open.
 *     Both ends of that window are device-clock readings: the SDK formats `time`
 *     from `allowRequestsAfter - Date.now()` (`index.esm.js:1264`, `:1345`,
 *     `:1469`), and this module stamps each delivery with `Date.now()`.
 *   - Whether the SDK holds a valid token is asked of the SDK itself, when a
 *     failure is read (`sdkHoldsValidToken`). The SDK drops an expired token
 *     without telling any listener, so no delivery can answer that — and neither
 *     can the token's JWT `exp` (round-2 B3). Google's clock stamps `exp`, while
 *     the SDK judges validity by an `expireTimeMillis` it stamps from the device
 *     clock at receipt (`:367-371`) and compares with `Date.now()` (`isValid`,
 *     `:1006-1008`). On a device whose clock lags Google's, `exp` would call a
 *     token valid after the SDK had dropped it. `addTokenListener` hands a newly
 *     added listener the token, in a microtask, exactly when that token passes
 *     `isValid` (`:878-890`), and makes no request doing so — so the answer is
 *     the SDK's own, on its own clock.
 *
 * What public surfaces cannot tell apart, and so is folded into `pending`
 * rather than guessed at: a reCAPTCHA `execute()` that never settles, a mint
 * that is merely slow, a mint that failed for a reason other than a 403/404
 * refusal, and a token the SDK has since dropped. None evidences a cause.
 */

export type AppCheckOutcome = 'ok' | 'rejected' | 'script-unavailable' | 'pending' | 'inactive';

export type AppCheckObservation =
  | { readonly kind: 'token'; readonly at: number; readonly refreshError?: unknown }
  | { readonly kind: 'error'; readonly at: number; readonly error: unknown };

export interface AppCheckSnapshot {
  /** App Check was initialized on this page. False on staging (no site key) or when init threw. */
  readonly active: boolean;
  /** The most recent listener delivery. Replaced wholesale, so identity changes only on change. */
  readonly last: AppCheckObservation | null;
}

interface AppCheckErrorLike {
  code?: unknown;
  customData?: { httpStatus?: unknown; time?: unknown };
}

type TokenSubscribe = (
  next: (result: { token?: string; internalError?: unknown } | null | undefined) => void,
  error: (error: unknown) => void,
) => unknown;

export const APP_CHECK_TOKEN_INVALID_CODE = 'auth/firebase-app-check-token-is-invalid';

const INITIAL_SNAPSHOT: AppCheckSnapshot = { active: false, last: null };
let snapshot: AppCheckSnapshot = INITIAL_SNAPSHOT;
let subscribeToSdk: TokenSubscribe | undefined;
const subscribers = new Set<() => void>();

function publish(next: AppCheckSnapshot): void {
  snapshot = next;
  subscribers.forEach((notify) => notify());
}

export function getAppCheckSnapshot(): AppCheckSnapshot {
  return snapshot;
}

export function subscribeAppCheckOutcome(notify: () => void): () => void {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}

/**
 * Wires one App Check instance into this module. Takes the subscribe call rather
 * than the instance so the production wiring and the real-SDK tests run the same
 * code, and so this module never imports firebase. The same call is how
 * `sdkHoldsValidToken` asks the SDK about its token.
 */
export function watchAppCheckOutcome(subscribe: TokenSubscribe, now: () => number = Date.now): void {
  subscribeToSdk = subscribe;
  publish({ active: true, last: snapshot.last });
  subscribe(
    (result) => publish({ active: true, last: { kind: 'token', at: now(), refreshError: result?.internalError } }),
    (error) => publish({ active: true, last: { kind: 'error', at: now(), error } }),
  );
}

export function __resetAppCheckOutcomeForTests(): void {
  snapshot = INITIAL_SNAPSHOT;
  subscribeToSdk = undefined;
}

/**
 * Microtask turns `sdkHoldsValidToken` yields before reading the SDK's answer.
 * The SDK needs one (`Promise.resolve().then(listener)`); the rest is margin. No
 * timer is involved, so faked timers cannot stall it.
 */
const SDK_ANSWER_TURNS = 3;

/**
 * Whether the SDK holds a valid App Check token right now, by its own clock
 * (round-2 B3). Adds a throwaway listener through the watched subscription and
 * removes it again: the SDK hands a newly added listener its token, in a
 * microtask, if and only if its in-memory token passes `isValid`
 * (`@firebase/app-check` `index.esm.js:878-890`). No request is made and no
 * mint is started.
 *
 * False when App Check is not watched on this page, and false when asking
 * throws: a question that could not be put is never an `ok`, and must not break
 * the failure path that asked it.
 */
export async function sdkHoldsValidToken(): Promise<boolean> {
  const subscribe = subscribeToSdk;
  if (subscribe === undefined) return false;
  let held = false;
  let unsubscribe: unknown;
  try {
    unsubscribe = subscribe(
      (result) => {
        if (typeof result?.token === 'string') held = true;
      },
      () => {},
    );
  } catch {
    return false;
  }
  for (let turn = 0; turn < SDK_ANSWER_TURNS; turn += 1) {
    await Promise.resolve();
  }
  try {
    if (typeof unsubscribe === 'function') unsubscribe();
  } catch {
    // The answer is already read; a listener that could not be removed only
    // receives later deliveries into a closure nothing reads.
  }
  return held;
}

export function isGrecaptchaPresent(): boolean {
  return typeof window !== 'undefined' && (window as { grecaptcha?: unknown }).grecaptcha != null;
}

/** The SDK's refusal throttle lives in page memory, so a reload is the one reset a user has. */
export function reloadForFreshAttestation(target: Pick<Location, 'reload'> = window.location): void {
  target.reload();
}

export function exchangeHttpStatus(error: unknown): number | undefined {
  const status = (error as AppCheckErrorLike | null | undefined)?.customData?.httpStatus;
  return typeof status === 'number' ? status : undefined;
}

function errorCode(error: unknown): string | undefined {
  const code = (error as AppCheckErrorLike | null | undefined)?.code;
  return typeof code === 'string' ? code : undefined;
}

const THROTTLE_CODES: ReadonlySet<string> = new Set(['appCheck/initial-throttle', 'appCheck/throttled']);
/** The statuses KB-3352 AC-1 defines as a refusal of attestation, as opposed to a transient failure. */
const REFUSAL_HTTP_STATUSES: ReadonlySet<number> = new Set([403, 404]);

export function isExchangeRefusal(error: unknown): boolean {
  const code = errorCode(error);
  const status = exchangeHttpStatus(error);
  return code !== undefined && THROTTLE_CODES.has(code) && status !== undefined && REFUSAL_HTTP_STATUSES.has(status);
}

/** The SDK's own `getDurationString` shape: `[DDd:][HHh:]MMm:SSs`. */
const THROTTLE_DURATION = /^(?:(\d+)d:)?(?:(\d+)h:)?(\d+)m:(\d+)s$/;

/**
 * How long the SDK said it will refuse to mint, read from the error it threw.
 * Undefined when the shape is not recognised — callers must then NOT claim a
 * throttle, because that claim is what tells a user retrying cannot help.
 */
export function throttleRemainingMs(error: unknown): number | undefined {
  const time = (error as AppCheckErrorLike | null | undefined)?.customData?.time;
  if (typeof time !== 'string') return undefined;
  const match = THROTTLE_DURATION.exec(time);
  if (!match) return undefined;
  const [, days = '0', hours = '0', minutes, seconds] = match;
  return (((Number(days) * 24 + Number(hours)) * 60 + Number(minutes)) * 60 + Number(seconds)) * 1000;
}

/**
 * True only while the page is inside the SDK's throttle after a 403/404 refusal:
 * every mint in this page now fails at once, so a sign-in cannot succeed until
 * the page is reloaded. This is the one state in which retrying is known to be
 * futile, and the only one `classifyAppCheckOutcome` calls `rejected`.
 */
export function isInsideRefusalThrottle(current: AppCheckSnapshot, now: number): boolean {
  const last = current.last;
  if (!current.active || last?.kind !== 'error' || !isExchangeRefusal(last.error)) return false;
  const remaining = throttleRemainingMs(last.error);
  return remaining !== undefined && now < last.at + remaining;
}

/**
 * `holdsValidToken` is the SDK's own answer (`sdkHoldsValidToken`), or undefined
 * when it was not asked — which is never enough for `ok`. A current refusal ranks
 * first; inside its window every mint short-circuits, so the SDK acquires no
 * valid token until the window lapses.
 */
export function classifyAppCheckOutcome(
  current: AppCheckSnapshot,
  grecaptchaPresent: boolean,
  now: number,
  holdsValidToken: boolean | undefined,
): AppCheckOutcome {
  if (!current.active) return 'inactive';
  if (isInsideRefusalThrottle(current, now)) return 'rejected';
  if (holdsValidToken === true) return 'ok';
  if (!grecaptchaPresent) return 'script-unavailable';
  return 'pending';
}

function describeError(error: unknown): string {
  const status = exchangeHttpStatus(error);
  return `${errorCode(error) ?? 'an error carrying no code'}, ${
    status === undefined ? 'no exchange HTTP status' : `exchange HTTP ${status}`
  }`;
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

export interface AppCheckEvidence {
  outcome: AppCheckOutcome;
  insideRefusalThrottle: boolean;
  message: string;
  data: {
    appcheck_outcome: AppCheckOutcome;
    grecaptcha_present: boolean;
    exchange_http_status?: number;
    app_check_error_code?: string;
    last_delivery_age_s?: number;
    sdk_holds_valid_token?: boolean;
  };
}

/**
 * Everything a failed login needs to render and report the attestation outcome.
 * The outcome is classified here, from the same inputs the message describes, so
 * the copy, the Sentry tag and the breadcrumb cannot disagree.
 */
export function describeAppCheckEvidence(
  current: AppCheckSnapshot,
  grecaptchaPresent: boolean,
  now: number,
  holdsValidToken: boolean | undefined,
): AppCheckEvidence {
  const outcome = classifyAppCheckOutcome(current, grecaptchaPresent, now, holdsValidToken);
  const last = current.last;
  const failure = last?.kind === 'error' ? last.error : last?.kind === 'token' ? last.refreshError : undefined;
  const askedTheSdk = current.active && holdsValidToken !== undefined;

  let detail: string;
  if (!current.active) {
    detail = 'App Check is not initialized on this page';
  } else if (last?.kind === 'error') {
    const remaining = isExchangeRefusal(last.error) ? throttleRemainingMs(last.error) : undefined;
    const throttleNote =
      remaining === undefined
        ? ''
        : now < last.at + remaining
          ? `; throttle window open until ${iso(last.at + remaining)}`
          : `; its throttle window lapsed at ${iso(last.at + remaining)}`;
    detail = `last App Check delivery was an error: ${describeError(last.error)}${throttleNote}`;
  } else if (last?.kind === 'token') {
    detail =
      last.refreshError === undefined
        ? 'last App Check delivery was a token'
        : `last App Check delivery was a token whose refresh failed: ${describeError(last.refreshError)}`;
  } else {
    detail = `no App Check token or error delivered yet; grecaptcha ${grecaptchaPresent ? 'present' : 'absent'}`;
  }
  if (askedTheSdk) {
    detail += holdsValidToken ? '; the SDK holds a valid token now' : '; the SDK holds no valid token now';
  }

  const status = failure === undefined ? undefined : exchangeHttpStatus(failure);
  const code = failure === undefined ? undefined : errorCode(failure);
  return {
    outcome,
    insideRefusalThrottle: isInsideRefusalThrottle(current, now),
    message: `App Check outcome: ${outcome} — ${detail}`,
    data: {
      appcheck_outcome: outcome,
      grecaptcha_present: grecaptchaPresent,
      ...(status === undefined ? {} : { exchange_http_status: status }),
      ...(code === undefined ? {} : { app_check_error_code: code }),
      ...(last === null ? {} : { last_delivery_age_s: Math.round((now - last.at) / 1000) }),
      ...(askedTheSdk ? { sdk_holds_valid_token: holdsValidToken === true } : {}),
    },
  };
}

/**
 * The fail-fast preflight's read (AC-3): synchronous, and it asks the SDK
 * nothing. A current refusal is all that path needs, and this read can never
 * produce `ok`.
 */
export function readRefusalEvidence(): AppCheckEvidence {
  return describeAppCheckEvidence(getAppCheckSnapshot(), isGrecaptchaPresent(), Date.now(), undefined);
}

/**
 * The failure paths' read: the SDK's own answer about its token, then this
 * page's snapshot, `window.grecaptcha` and the clock — read once, after the
 * answer, so nothing delivered while asking is missed.
 */
export async function readAppCheckEvidence(): Promise<AppCheckEvidence> {
  const holdsValidToken = await sdkHoldsValidToken();
  return describeAppCheckEvidence(getAppCheckSnapshot(), isGrecaptchaPresent(), Date.now(), holdsValidToken);
}
